// src/components/GST/VendorSummaryGrid.js
import React, { useState } from "react";
import { Table, Button, Form, Spinner } from "react-bootstrap";

/* ================= styles (MATCHRESULTSGRID CONSISTENT) ================= */

const compactCell = {
  padding: "1px 4px",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const stickyHeader = {
  position: "sticky",
  top: 0,
  background: "#f8f9fa",
  zIndex: 2,
};

const diffStyle = {
  color: "#d10000",
};

/* ================= helpers ================= */

const f2 = (v) => Number(v || 0).toFixed(2);

const isNumDiffTol = (a, b, tol) =>
  Math.abs(Number(a || 0) - Number(b || 0)) > tol;

/* ================= component ================= */

const VendorSummaryGrid = ({
  data = [],
  loading = false,
  onView,
  onApprove,
  onUnmatch,
}) => {
  const [selected, setSelected] = useState({});
  const [filterMode, setFilterMode] = useState("ALL"); // ALL | DIFF | MATCHED
  const [tolerance, setTolerance] = useState(0);       // ₹0–₹10

  /* ---------- selection ---------- */

  const allSelected =
    data.length > 0 && data.every((_, i) => selected[i]);

  const selectedCount = Object.values(selected).filter(Boolean).length;

  const toggleSelectAll = (checked) => {
    const next = {};
    if (checked) data.forEach((_, i) => (next[i] = true));
    setSelected(next);
  };

  const toggleRowSelect = (idx) =>
    setSelected((p) => ({ ...p, [idx]: !p[idx] }));

  /* ---------- filtering ---------- */

  const filteredData = data.filter((d) => {
    const taxDiff = isNumDiffTol(d.TAXABLE_VALUE_2B, d.ASSESABLE_VALUE_ERP, tolerance);
    const igstDiff = isNumDiffTol(d.IGST_2B, d.ERP_IGST, tolerance);
    const cgstDiff = isNumDiffTol(d.CGST_2B, d.ERP_CGST, tolerance);
    const sgstDiff = isNumDiffTol(d.SGST_2B, d.ERP_SGST, tolerance);

    const hasDiff = taxDiff || igstDiff || cgstDiff || sgstDiff;

    if (filterMode === "DIFF") return hasDiff;
    if (filterMode === "MATCHED") return !hasDiff;
    return true;
  });

  /* ================= render ================= */

  return (
    <>
      {/* ===== Top Control Bar (MATCHRESULTSGRID STYLE) ===== */}
      <div
        style={{
          marginBottom: "4px",
          padding: "3px 6px",
          border: "1px solid #ddd",
          borderRadius: "4px",
          background: "#f8f9fa",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "0.62rem",
        }}
      >
        {/* LEFT */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Form.Check
            type="checkbox"
            checked={allSelected}
            onChange={(e) => toggleSelectAll(e.target.checked)}
            label="All"
          />

          {selectedCount > 0 && <b>{selectedCount} selected</b>}

          <Button
            size="sm"
            variant="success"
            disabled={selectedCount === 0}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() =>
              onApprove &&
              onApprove(filteredData.filter((_, i) => selected[i]))
            }
          >
            Approve
          </Button>

          <Button
            size="sm"
            variant="warning"
            disabled={selectedCount === 0}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() =>
              onUnmatch &&
              onUnmatch(filteredData.filter((_, i) => selected[i]))
            }
          >
            Unmatch
          </Button>
        </div>

        {/* RIGHT */}
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span>Tol:</span>

          <Button
            size="sm"
            variant="outline-secondary"
            style={{ fontSize: "0.6rem", padding: "0 4px" }}
            onClick={() => setTolerance((t) => Math.max(0, t - 1))}
          >
            ←
          </Button>

          <span style={{ minWidth: "12px", textAlign: "center" }}>
            {tolerance}
          </span>

          <Button
            size="sm"
            variant="outline-secondary"
            style={{ fontSize: "0.6rem", padding: "0 4px" }}
            onClick={() => setTolerance((t) => Math.min(10, t + 1))}
          >
            →
          </Button>

          <Button
            size="sm"
            variant={filterMode === "ALL" ? "secondary" : "outline-secondary"}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => setFilterMode("ALL")}
          >
            All
          </Button>

          <Button
            size="sm"
            variant={filterMode === "DIFF" ? "danger" : "outline-danger"}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => setFilterMode("DIFF")}
          >
            Diff
          </Button>

          <Button
            size="sm"
            variant={filterMode === "MATCHED" ? "success" : "outline-success"}
            style={{ fontSize: "0.6rem", padding: "1px 6px" }}
            onClick={() => setFilterMode("MATCHED")}
          >
            Matched
          </Button>
        </div>
      </div>

      {/* ===== Grid ===== */}
      <div
        style={{
          maxHeight: "380px",
          overflow: "auto",
          border: "1px solid #ddd",
          borderRadius: "4px",
        }}
      >
        <Table bordered hover size="sm" style={{ fontSize: "0.62rem", margin: 0 }}>
          <thead>
            <tr>
              <th style={{ ...compactCell, ...stickyHeader }}></th>
              <th style={{ ...compactCell, ...stickyHeader }}>GSTIN</th>
              <th style={{ ...compactCell, ...stickyHeader }}>2B Vendor</th>
              <th style={{ ...compactCell, ...stickyHeader }}>ERP Vendor</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>Rec</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>Tax 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>Tax ERP</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>IGST 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>IGST ERP</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CGST 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>CGST ERP</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>SGST 2B</th>
              <th className="text-end" style={{ ...compactCell, ...stickyHeader }}>SGST ERP</th>
              <th style={{ ...compactCell, ...stickyHeader }}>Remarks</th>
              <th style={{ ...compactCell, ...stickyHeader }}>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={15} style={{ ...compactCell, textAlign: "center" }}>
                  <Spinner size="sm" /> Loading vendor summary…
                </td>
              </tr>
            )}

            {!loading && filteredData.length === 0 && (
              <tr>
                <td colSpan={15} style={{ ...compactCell, textAlign: "center" }}>
                  No records
                </td>
              </tr>
            )}

            {filteredData.map((d, idx) => {
              const taxDiff = isNumDiffTol(d.TAXABLE_VALUE_2B, d.ASSESABLE_VALUE_ERP, tolerance);
              const igstDiff = isNumDiffTol(d.IGST_2B, d.ERP_IGST, tolerance);
              const cgstDiff = isNumDiffTol(d.CGST_2B, d.ERP_CGST, tolerance);
              const sgstDiff = isNumDiffTol(d.SGST_2B, d.ERP_SGST, tolerance);

              return (
                <tr key={idx} style={{ height: "20px" }}>
                  <td style={compactCell}>
                    <Form.Check
                      type="checkbox"
                      checked={!!selected[idx]}
                      onChange={() => toggleRowSelect(idx)}
                    />
                  </td>

                  <td style={compactCell}>{d.GSTIN}</td>
                  <td style={compactCell}>{d.PARTY_NAME_2B}</td>
                  <td style={compactCell}>{d.PARTY_NAME_ERP}</td>
                  <td className="text-end" style={compactCell}>{d.REC_CNT}</td>

                  <td className="text-end" style={{ ...compactCell, ...(taxDiff ? diffStyle : {}) }}>
                    {f2(d.TAXABLE_VALUE_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(taxDiff ? diffStyle : {}) }}>
                    {f2(d.ASSESABLE_VALUE_ERP)}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(igstDiff ? diffStyle : {}) }}>
                    {f2(d.IGST_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(igstDiff ? diffStyle : {}) }}>
                    {f2(d.ERP_IGST)}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(cgstDiff ? diffStyle : {}) }}>
                    {f2(d.CGST_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(cgstDiff ? diffStyle : {}) }}>
                    {f2(d.ERP_CGST)}
                  </td>

                  <td className="text-end" style={{ ...compactCell, ...(sgstDiff ? diffStyle : {}) }}>
                    {f2(d.SGST_2B)}
                  </td>
                  <td className="text-end" style={{ ...compactCell, ...(sgstDiff ? diffStyle : {}) }}>
                    {f2(d.ERP_SGST)}
                  </td>

                  <td style={compactCell}>{d.REMARKS}</td>

                  <td style={compactCell}>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      style={{ fontSize: "0.6rem", padding: "1px 6px" }}
                      onClick={() => onView && onView(d)}
                    >
                      View
                    </Button>

                    <Button
                      size="sm"
                      variant="success"
                      style={{ fontSize: "0.6rem", padding: "1px 6px", marginLeft: "2px" }}
                      onClick={() => onApprove && onApprove(d)}
                    >
                      Approve
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-secondary"
                      style={{ fontSize: "0.6rem", padding: "1px 6px", marginLeft: "2px" }}
                      onClick={() => onUnmatch && onUnmatch(d)}
                    >
                      Unmatch
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default VendorSummaryGrid;
